import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import { API } from "@/apiConfig";
import { applyPublicSeo, buildPublicSeo } from "@/lib/publicSeo";
import { applyTheme, DEFAULT_THEME_ID } from "@/themes";

const PublicSiteContext = createContext(null);

export function PublicSiteProvider({ children }) {
  const location = useLocation();
  const previewTokenRef = useRef(new URLSearchParams(window.location.search).get("site_plan_preview") || "");
  const [snapshot, setSnapshot] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const previewToken = previewTokenRef.current;
    const requestConfig = previewToken ? { params: { site_plan_preview: previewToken } } : undefined;
    const requestKey = `${location.pathname}|${previewToken}`;
    setSnapshot(null);
    setFailed(false);
    Promise.all([
      axios.get(`${API}/public/site-plan`, requestConfig),
      axios.get(`${API}/public/settings`, requestConfig),
    ]).then(([manifestResponse, settingsResponse]) => {
      if (!active) return;
      const manifest = manifestResponse.data || { pages: [], dynamic_templates: [] };
      const settings = settingsResponse.data || {};
      const previewTheme = new URLSearchParams(window.location.search).get("themePreview");
      applyTheme(previewTheme || settings.active_theme || DEFAULT_THEME_ID);
      const selected = (manifest.pages || []).find(page => page.enabled !== false && page.route === location.pathname);
      const source = selected?.aliasOf ? (manifest.pages || []).find(page => page.id === selected.aliasOf) : selected;
      const slug = source?.cmsSlug;
      const pageRequest = slug ? axios.get(`${API}/public/pages/${slug}`, requestConfig) : Promise.resolve({ data: null });
      return pageRequest.then(pageResponse => {
        if (!active) return;
        applyPublicSeo(buildPublicSeo({ pageDocument: pageResponse.data || null, pathname: location.pathname, settings }));
        setSnapshot({ manifest, settings, pageDocument: pageResponse.data || null, pathname: location.pathname, requestKey });
      });
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => { active = false; };
  }, [location.pathname, location.search]);

  if (failed) return <section className="flex min-h-screen items-center justify-center bg-white px-6"><div className="max-w-lg text-center"><h1 className="text-2xl font-semibold text-slate-800">Vitrine momentanément indisponible</h1><p className="mt-3 text-sm text-slate-600">La configuration active du site ne peut pas être chargée.</p></div></section>;
  const expectedRequestKey = `${location.pathname}|${previewTokenRef.current}`;
  if (!snapshot || snapshot.requestKey !== expectedRequestKey) return <div className="min-h-screen bg-white" aria-label="Chargement de la configuration demandée"/>;

  return <PublicSiteContext.Provider value={snapshot}>{children}</PublicSiteContext.Provider>;
}

export function usePublicSite() {
  const value = useContext(PublicSiteContext);
  if (!value) throw new Error("usePublicSite doit être utilisé dans PublicSiteProvider");
  return value;
}
